import moment from 'moment';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import Dice from '@/models/Dice';
import Sortie from '@/models/Sortie';
import PilotStatus from '@/models/PilotStatus';
import PilotNames from '@/models/PilotNames';
import PilotQuality from '@/models/PilotQuality';
import PilotRank from '@/models/PilotRank';
import FinestHourAircraft from '@/models/FinestHourAircraft';
import DailyEvent from '@/models/finestHour/DailyEvent';
import Groups from '@/models/finestHour/Groups';
import SortieLocations from '@/models/finestHour/SortieLocations';
import HomeAirfields from '@/models/finestHour/HomeAirfields';
import Aircrafts from '@/models/finestHour/Aircrafts';
import allAircrafts from '@/models/Aircrafts';
import Countries from '@/models/Countries';
import Pilot from '@/models/Pilot';
import Squadron from '@/models/Squadron';

const EmptyPilotList = [{
  name: '"A" flight',
  sections: [{
    name: 'Red',
    squadronSlots: [],
  }, {
    name: 'Yellow',
    squadronSlots: [],
  }],
}, {
  name: '"B" flight',
  sections: [{
    name: 'Blue',
    squadronSlots: [],
  }, {
    name: 'Green',
    squadronSlots: [],
  }],
}];

export default class FinestHour {
  toJSON() {
    return JSON.stringify({
      squadronNumber: this.squadronNumber,
      groupID: this.groupID,
      group: this.group,
      homeAirfield: this.homeAirfield,
      aircraft: this.aircraft,
      numberOfAircrafts: this.numberOfAircrafts,
      aircrafts: this.aircrafts.map((x) => x.toPOJO()),
      originalNumberOfPilots: this.originalNumberOfPilots,
      currentDate: this.currentDate.format(),
      squadron: this.squadron,
      pilots: this.pilots,
      sorties: this.sorties.map((x) => x.toPOJO()),
      dailyEvents: this.dailyEvents.map((x) => x.toPOJO()),
      pilotNameIndex: this.pilotNameIndex,
    });
  }

  static fromJSON(json) {
    return new FinestHour(JSON.parse(json));
  }

  constructor({
    squadronNumber = null,
    groupID = null,
    group = null,
    homeAirfield = null,
    aircraft = null,
    numberOfAircrafts = null,
    aircrafts = [],
    originalNumberOfPilots = null,
    currentDate = moment('1940-07-10'),
    squadron = JSON.parse(JSON.stringify(EmptyPilotList)),
    pilots = [],
    sorties = [],
    dailyEvents = [],
    pilotNameIndex = -1,
  } = {}) {
    this.squadronNumber = squadronNumber;
    this.groupID = groupID;
    this.group = group;
    this.homeAirfield = homeAirfield;
    this.aircraft = aircraft;
    this.sorties = sorties.map((x) => Sortie.fromPOJO(x));
    this.numberOfAircrafts = numberOfAircrafts;
    this.aircrafts = aircrafts.map((x) => FinestHourAircraft.fromPOJO(x));
    this.originalNumberOfPilots = originalNumberOfPilots;
    this.currentDate = moment(currentDate);
    this.squadron = squadron;
    this.pilots = pilots;
    this.sorties = sorties.map((x) => Sortie.fromPOJO(x));
    this.dailyEvents = dailyEvents.map((x) => DailyEvent.fromPOJO(x));
    this.pilotNameIndex = pilotNameIndex;

    if (squadronNumber == null) {
      this.generateSquadronNumber();
    }
    if (groupID == null) {
      this.generateGroup();
    }
    if (homeAirfield == null) {
      this.generateHomeAirfield();
    }
    if (aircraft == null) {
      this.generateAircraft();
    }
    if (numberOfAircrafts == null) {
      this.generateNumberOfAircrafts();
    } else if (this.sorties.length === 0) {
      for (let i = 0; i < this.numberOfAircrafts; i += 1) {
        this.aircrafts.push(new FinestHourAircraft());
      }
    }
    if (originalNumberOfPilots == null) {
      this.generateOriginalNumberOfPilots();
    }
    if (sorties.length === 0) {
      // Clear aircrafts
      this.pilots.forEach((x) => {
        x.aircraft = null; // eslint-disable-line
      });

      this.squadron[0].sections[0].squadronSlots.push({
        id: uuidv4(),
        type: 'Squadron Leader',
        position: 1,
        pilot: this.generateSquadronLeader(),
      });
      this.squadron[0].sections[0].squadronSlots.push({
        id: uuidv4(),
        position: 2,
        pilot: this.generateSectionPilot(),
      });
      this.squadron[0].sections[0].squadronSlots.push({
        id: uuidv4(),
        position: 3,
        pilot: this.generateSectionPilot(),
      });
      this.squadron[0].sections[1].squadronSlots.push({
        id: uuidv4(),
        position: 1,
        pilot: this.generateSectionLeader(),
      });
      this.squadron[0].sections[1].squadronSlots.push({
        id: uuidv4(),
        position: 2,
        pilot: this.generateSectionPilot(),
      });
      this.squadron[0].sections[1].squadronSlots.push({
        id: uuidv4(),
        position: 3,
        pilot: this.generateSectionPilot(),
      });
      this.squadron[1].sections[0].squadronSlots.push({
        id: uuidv4(),
        type: 'Flight Leader',
        position: 1,
        pilot: this.generateFlightLeader(),
      });
      this.squadron[1].sections[0].squadronSlots.push({
        id: uuidv4(),
        position: 2,
        pilot: this.generateSectionPilot(),
      });
      this.squadron[1].sections[0].squadronSlots.push({
        id: uuidv4(),
        position: 3,
        pilot: this.generateSectionPilot(),
      });
      this.squadron[1].sections[1].squadronSlots.push({
        id: uuidv4(),
        position: 1,
        pilot: this.generateSectionLeader(),
      });
      this.squadron[1].sections[1].squadronSlots.push({
        id: uuidv4(),
        position: 2,
        pilot: this.generateSectionPilot(),
      });
      this.squadron[1].sections[1].squadronSlots.push({
        id: uuidv4(),
        position: 3,
        pilot: this.generateSectionPilot(),
      });

      for (let i = this.pilots.length; i < this.originalNumberOfPilots; i += 1) {
        this.generateSectionPilot();
      }

      this.generateSortie();

      this.save();
    } else {
      this.squadron.forEach((flight) => {
        flight.sections.forEach((section) => {
          section.squadronSlots = section.squadronSlots.map((x) => // eslint-disable-line
            ({// eslint-disable-line
              ...x,
              pilot: this.pilots.find((p) => x.pilot != null && p.id === x.pilot.id),
            }));
        });
      });
    }
  }

  get currentPhase() {
    if (this.currentDate.isSameOrBefore(moment('1940-08-07'))) {
      return 1;
    }
    if (this.currentDate.isSameOrBefore(moment('1940-09-06'))) {
      return 2;
    }
    if (this.currentDate.isSameOrBefore(moment('1940-10-5'))) {
      return 3;
    }
    return 4;
  }

  get lastSortie() {
    return this.sorties.length > 0
      ? this.sorties[0]
      : null;
  }

  get activeAircrafts() {
    return this.aircrafts.filter((x) => x.isActive);
  }

  get destroyedAircrafts() {
    return this.aircrafts.filter((x) => x.isDestroyed);
  }

  get aircraftsUnderRepair() {
    return this.aircrafts.filter((x) => !x.isDestroyed && x.isUnderRepair);
  }

  get activePilots() {
    return this.pilots.filter((x) => x.status === PilotStatus.active);
  }

  get squadronSlots() {
    return this.squadron.reduce((mem0, flight) => [...mem0,
      ...flight.sections.reduce((mem1, section) => [...mem1, ...section.squadronSlots], [])], []);
  }

  get squadronSections() {
    return this.squadron.reduce((mem0, flight) => [...mem0,
      ...flight.sections], []);
  }

  get activeSections() {
    const result = this.squadronSections
      .filter((x) => x.squadronSlots
        .some((s) => this.lastSortie.activeSlotsIDs.some((id) => id === s.id)));
    const squadron = result.map((x) => ({
      name: x.name,
      color: x.name,
      country: Countries.find((c) => c.name === 'Great Britain'),
      pilots: x.squadronSlots.filter((ss) => this.lastSortie.activeSlotsIDs
        .some((id) => id === ss.id))
        .map((ss, i) => (new Pilot({
          id: i + 1,
          name: ss.pilot.name,
          rank: ss.pilot.quality,
          aircraft: allAircrafts.find((a) => a.type === this.aircraft),
        }))),
    }));
    return squadron;
  }

  get emptyActiveSlots() {
    return this.lastSortie.activeSlotsIDs
      .map((id) => this.squadronSlots.find((x) => x.id === id))
      .filter((x) => x.pilot == null);
  }

  get nonEmptySlots() {
    return this.squadronSlots
      .filter((x) => x.pilot != null);
  }

  get emptySlots() {
    return this.squadronSlots
      .filter((x) => x.pilot == null);
  }

  get nonEmptyInactiveSlots() {
    return this.squadronSlots
      .filter((x) => !this.lastSortie.activeSlotsIDs.includes(x.id) && x.pilot != null);
  }

  get nonEmptyActiveSlots() {
    return this.squadronSlots
      .filter((x) => this.lastSortie.activeSlotsIDs.includes(x.id) && x.pilot != null);
  }

  get unassignedPilots() {
    return this.pilots.filter((x) => x.status === PilotStatus.active
      && !this.nonEmptySlots.find((y) => x.id === y.pilot.id));
  }

  get killedPilots() {
    return this.pilots.filter((x) => x.status === PilotStatus.killed);
  }

  get transferredPilots() {
    return this.pilots.filter((x) => x.status === PilotStatus.transferred);
  }

  get injuredPilots() {
    return this.pilots.filter((x) => x.status === PilotStatus.injured);
  }

  get lostPilots() {
    return this.pilots.filter((x) => x.status === PilotStatus.lost);
  }

  get capturedPilots() {
    return this.pilots.filter((x) => x.status === PilotStatus.captured);
  }

  get onLeavePilots() {
    return this.pilots.filter((x) => x.status === PilotStatus.onLeave);
  }

  get inactivePilots() {
    return [
      ...this.onLeavePilots,
      ...this.transferredPilots,
      ...this.injuredPilots,
      ...this.killedPilots,
      ...this.capturedPilots,
      ...this.lostPilots,
    ];
  }

  get inactivePilotsByStatus() {
    return [{
      name: 'On leave',
      pilots: this.onLeavePilots,
    }, {
      name: 'Transferred',
      pilots: this.transferredPilots,
    }, {
      name: 'Injured',
      pilots: this.injuredPilots,
    }, {
      name: 'Killed',
      pilots: this.killedPilots,
    }, {
      name: 'Captured',
      pilots: this.capturedPilots,
    }, {
      name: 'Lost',
      pilots: this.lostPilots,
    }];
  }

  get activeAircraftsCount() {
    return this.activeAircrafts.length
      - this.lastSortie.destroyedAircrafts
      - this.lastSortie.damagedAircrafts;
  }

  get aircraftsUnderRepairCount() {
    return this.aircraftsUnderRepair.length + this.lastSortie.damagedAircrafts;
  }

  save() {
    window.localStorage.finestHour = this.toJSON();
    if (window.localStorage.uuid) {
      axios.put(`http://apps.iwouldliketorage.com:49160/api/finestHours/${window.localStorage.uuid}`, {
        json: this.toJSON(),
      });
    }
  }

  static load() {
    if (window.localStorage.finestHour != null) {
      return FinestHour.fromJSON(window.localStorage.finestHour);
    }
    return null;
  }

  generateSquadronNumber() {
    let number = Dice.d100();
    if (Math.floor(number / 10) === number % 10) {
      number = 100 + Dice.d100();
    }
    this.squadronNumber = number;
  }

  generateGroup() {
    const n = Dice.d100();
    let group = null;
    if (n <= 10) {
      [group] = Groups;
    } else if (n <= 49) {
      [, group] = Groups;
    } else if (n <= 75) {
      [, , group] = Groups;
    } else {
      [, , , group] = Groups;
    }
    this.groupID = group.id;
    this.group = group.name;
  }

  generateHomeAirfield() {
    const i = Dice.d6(2) - 2;
    this.homeAirfield = HomeAirfields[this.groupID][i];
  }

  generateAircraft() {
    const i = Dice.d6(2) - 2;
    this.aircraft = Aircrafts[this.groupID][i];
  }

  generateNumberOfAircrafts() {
    this.numberOfAircrafts = 12 + Dice.d6();
    for (let i = 0; i < this.numberOfAircrafts; i += 1) {
      this.aircrafts.push(new FinestHourAircraft());
    }
  }

  generateOriginalNumberOfPilots() {
    let originalNumberOfPilots = 10 + Dice.d6(2);
    switch (this.currentPhase) {
      case 1:
        originalNumberOfPilots -= 1;
        break;
      case 2:
        originalNumberOfPilots -= 3;
        break;
      case 3:
        originalNumberOfPilots -= 2;
        break;
      case 4:
        originalNumberOfPilots -= 1;
        break;
      default:
        break;
    }
    this.originalNumberOfPilots = originalNumberOfPilots;
  }

  generateSquadronLeader() {
    let table = null;
    switch (this.currentPhase) {
      case 1:
      case 2:
      case 3:
        table = ['TA', 'JA', 'JA', 'VET', 'VET', 'VET', 'VET', 'VET', 'REG', 'REG'];
        break;
      case 4:
        table = ['TA', 'JA', 'JA', 'VET', 'VET', 'VET', 'VET', 'VET', 'REG', 'REG'];
        break;
      default:
        break;
    }
    const quality = table[Dice.d10() - 1];
    const pilot = {
      id: uuidv4(),
      name: PilotNames[this.pilotNameIndex += 1],
      quality,
      rank: 'Flight Lieutenant',
      status: PilotStatus.active,
    };
    this.pilots.push(pilot);
    return pilot;
  }

  generateFlightLeader() {
    let table = null;
    switch (this.currentPhase) {
      case 1:
      case 2:
      case 3:
        table = ['TA', 'JA', 'JA', 'VET', 'VET', 'VET', 'REG', 'REG', 'REG', 'REG'];
        break;
      case 4:
        table = ['TA', 'JA', 'JA', 'VET', 'VET', 'VET', 'REG', 'REG', 'REG', 'REG'];
        break;
      default:
        break;
    }
    const quality = table[Dice.d10() - 1];
    const rank = FinestHour.generateRank(quality);
    const pilot = {
      id: uuidv4(),
      name: PilotNames[this.pilotNameIndex += 1],
      status: PilotStatus.active,
      rank,
      quality,
    };
    this.pilots.push(pilot);
    return pilot;
  }

  generateSectionLeader() {
    let table = null;
    switch (this.currentPhase) {
      case 1:
      case 2:
      case 3:
        table = ['JA', 'JA', 'VET', 'VET', 'VET', 'REG', 'REG', 'REG', 'Sprog', 'Sprog'];
        break;
      case 4:
        table = ['JA', 'JA', 'VET', 'VET', 'VET', 'REG', 'REG', 'REG', 'REG', 'REG'];
        break;
      default:
        break;
    }
    const quality = table[Dice.d10() - 1];
    const rank = FinestHour.generateRank(quality);
    const pilot = {
      id: uuidv4(),
      name: PilotNames[this.pilotNameIndex += 1],
      status: PilotStatus.active,
      rank,
      quality,
    };
    this.pilots.push(pilot);
    return pilot;
  }

  generateSectionPilot() {
    let table = null;
    switch (this.currentPhase) {
      case 1:
      case 2:
      case 3:
        table = ['REG', 'REG', 'REG', 'REG', 'Sprog', 'Sprog', 'Sprog', 'Sprog', 'Sprog', 'Sprog'];
        break;
      case 4:
        table = ['REG', 'REG', 'REG', 'REG', 'REG', 'Sprog', 'Sprog', 'Sprog', 'Sprog', 'Sprog'];
        break;
      default:
        break;
    }
    const quality = table[Dice.d10() - 1];
    const rank = FinestHour.generateRank(quality);
    const pilot = {
      id: uuidv4(),
      name: PilotNames[this.pilotNameIndex += 1],
      status: PilotStatus.active,
      rank,
      quality,
    };
    this.pilots.push(pilot);
    return pilot;
  }

  static generateRank(quality) {
    let roll = Dice.d6();
    if (quality === 'TA' || quality === 'JA') {
      roll += 2;
    } else if (quality === 'Sprog') {
      roll -= 2;
    }
    return PilotRank[Math.min(7, Math.max(0, roll - 1))];
  }

  generateSortie() {
    let roll1 = Dice.d6();
    let roll2 = Dice.d6();
    let roll3 = Dice.d6();
    let roll4 = Dice.d6();

    switch (this.currentPhase) {
      case 1:
        roll1 = Math.max(1, roll1 - 2);
        roll2 = Math.max(1, roll2 - 2);
        roll3 = Math.max(1, roll3 - 2);
        break;
      case 2:
        roll1 = Math.max(1, roll1 - 1);
        roll3 = Math.max(1, roll3 - 1);
        break;
      case 4:
        roll1 = Math.min(6, roll1 + 1);
        roll2 = Math.min(6, roll2 + 1);
        roll3 = Math.min(6, roll3 + 1);
        roll4 = Math.min(6, roll4 + 1);
        break;
      default:
        break;
    }
    const sortie = { date: moment(this.currentDate) };
    const rafStrengths = [
      'A single plane',
      'A pair',
      'One section',
      'Two sections (flight)',
      'One flight',
      'Two sections (flight)',
      'A full squadron',
      'A full squadron',
    ];
    sortie.rafStrength = rafStrengths[roll1 - 1];
    const missionTypes = [
      'bounced by enemy fighters',
      'vectored onto reconnaissance aircraft',
      'bounced by enemy fighters',
      'about to bounce an enemy formation',
      'encounters unescorted bombers',
      'scrambled to intercept enemy raid',
      'scrambled to intercept',
      'scrambled to intercept',
    ];
    sortie.missionType = missionTypes[roll2 - 1];

    const sunDirection = ['West', 'South West', 'South East', 'East'];
    let rafBand = null;
    let luftwaffeBand = null;
    sortie.notes = '';
    sortie.reinforcementsPossible = false;
    switch (sortie.missionType) {
      case 'about to bounce an enemy formation':
        luftwaffeBand = Dice.d3() + 2;
        break;
      case 'bounced by enemy fighters':
        rafBand = Dice.d3() + 2;
        break;
      case 'encounters unescorted bombers':
        sortie.reinforcementsPossible = true;
        break;
      case 'scrambled to intercept':
      case 'scrambled to intercept enemy raid':
        rafBand = Dice.d6();
        sortie.notes = `RAF approaching from ${['west', 'north', 'east'][Dice.d3() - 1]}, `;
        break;
      case 'vectored onto reconnaissance aircraft':
        sortie.reinforcementsPossible = true;
        rafBand = Dice.d6();
        luftwaffeBand = Dice.d3() + 3;
        break;
      default:
        break;
    }

    const getBandAccordingToEnemy = (enemyBand) => {
      switch (Dice.d6()) {
        case 1:
          return Math.max(1, enemyBand - 2);
        case 2:
          return Math.max(1, enemyBand - 1);
        case 3:
        case 4:
          return enemyBand;
        case 5:
          return Math.min(6, enemyBand + 1);
        case 6:
          return Math.min(6, enemyBand + 2);
        default:
          return enemyBand;
      }
    };

    if (rafBand == null && luftwaffeBand == null) {
      rafBand = Dice.d6();
      luftwaffeBand = getBandAccordingToEnemy(rafBand);
    } else if (rafBand == null) {
      rafBand = getBandAccordingToEnemy(luftwaffeBand);
    } else {
      luftwaffeBand = getBandAccordingToEnemy(rafBand);
    }
    sortie.notes += `Sun is ${sunDirection[Dice.d3() - 1]}, RAF band is ${rafBand}, Luftwaffe band is ${luftwaffeBand}`;

    const bomberCounts = [3, 3, 6, 9, 12, 15, 18, 3];
    sortie.bomberCount = bomberCounts[roll3 - 1];

    const luftwaffeFighterCounts = [
      'None',
      'One schwarm',
      'Two schwarm',
      'Two schwarm',
      'Three schwarm',
      'Three schwarm',
      'Staffel',
      'Staffel',
    ];
    sortie.luftwaffeFighterCount = this.group.includes('13')
      ? 'None'
      : luftwaffeFighterCounts[roll4 - 1];

    if ((sortie.missionType === 'bounced by enemy fighters'
      || sortie.missionType === 'about to bounce an enemy formation')
      && sortie.luftwaffeFighterCount === 'None') {
      sortie.luftwaffeFighterCount = luftwaffeFighterCounts[roll4];
    }

    switch (sortie.rafStrength) {
      case 'A single plane':
        sortie.activeSlotsIDs = [this.squadron[Dice.d2() - 1]
          .sections[Dice.d2() - 1]
          .squadronSlots[Dice.d3() - 1].id];
        break;
      case 'A pair':
        sortie.activeSlotsIDs = this.squadron[Dice.d2() - 1]
          .sections[Dice.d2() - 1].squadronSlots.slice(0, 2)
          .map((x) => x.id);
        break;
      case 'One section':
        sortie.activeSlotsIDs = this.squadron[Dice.d2() - 1]
          .sections[Dice.d2() - 1].squadronSlots
          .map((x) => x.id);
        break;
      case 'Two sections (flight)':
        sortie.activeSlotsIDs = [
          ...this.squadron[0].sections[Dice.d2() - 1].squadronSlots.map((x) => x.id),
          ...this.squadron[1].sections[Dice.d2() - 1].squadronSlots.map((x) => x.id),
        ];
        break;
      case 'One flight':
        sortie.activeSlotsIDs = this.squadron[Dice.d2() - 1]
          .sections.reduce((mem, s) => [...mem, ...s.squadronSlots.map((x) => x.id)], []);
        break;
      case 'A full squadron':
        sortie.activeSlotsIDs = [
          ...this.squadron[0].sections[0].squadronSlots.map((x) => x.id),
          ...this.squadron[0].sections[1].squadronSlots.map((x) => x.id),
          ...this.squadron[1].sections[0].squadronSlots.map((x) => x.id),
          ...this.squadron[1].sections[1].squadronSlots.map((x) => x.id),
        ];
        break;
      default:
        break;
    }
    // What type of bandit?
    const rollAircraftType = () => Math.min(5, Math.max(0, (Dice.d6() - 1)
      + (this.currentPhase === 1 ? -1 : 0)
      + (this.currentPhase === 3 ? 1 : 0)
      + (this.currentPhase === 4 ? 2 : 0)));
    sortie.bomberType = ['Ju-87B/D Stuka', 'Ju-87B/D Stuka', 'He111', 'Ju-88A-1', 'He111', 'Do17-E'][rollAircraftType()];
    sortie.escortType = ['BF110C', 'BF110C', 'BF110C', 'BF109E-1', 'BF109E-1', 'BF109E-1'][rollAircraftType()];
    sortie.reconnaissanceType = ['BF110C', 'Ju-88A-1', 'Do17-E', 'Do17-E', 'Do17-E', 'BF109E-1'][rollAircraftType()];
    sortie.reinforcementType = ['BF110C', 'BF110C', 'BF110C', 'BF109E-1', 'BF109E-1', 'BF109E-1'][rollAircraftType()];

    const colors = ['Red', 'Blue', 'Green', 'Yellow', 'Black', 'Grey'];

    let escortSchwarms = 0;
    switch (sortie.luftwaffeFighterCount) {
      case 'One schwarm':
        escortSchwarms = 1;
        break;
      case 'Two schwarm':
        escortSchwarms = 2;
        break;
      case 'Three schwarm':
        escortSchwarms = 3;
        break;
      case 'Staffel':
        escortSchwarms = 4;
        break;
      default:
        break;
    }

    const bomberCount = !sortie.missionType.includes('bounce')
    && !sortie.missionType.includes('vectored')
      ? sortie.bomberCount : 0;
    const reconCount = sortie.missionType.includes('recon') ? 1 : 0;
    escortSchwarms = !sortie.missionType.includes('recon') ? escortSchwarms : 0;

    const germany = Countries.find((c) => c.name === 'Germany');
    const germanSections = [
      ...(reconCount > 0
        ? [new Squadron({
          country: germany,
          name: 'Recon 1',
          color: colors[0],
          pilots: new Array(reconCount).fill(0)
            .map((x, i) => ({
              id: i + 1,
              name: `Pilot ${i + 1}`,
              rank: FinestHour.getRandomGermanPilotQuality(1),
              aircraftType: allAircrafts.find((a) => a.type === sortie.reconnaissanceType).type,
            })),
        })]
        : []),
      ...(bomberCount > 0
        ? [new Squadron({
          country: germany,
          name: 'Schwarm 1',
          color: colors[0],
          pilots: new Array(bomberCount).fill(0)
            .map((x, i) => ({
              id: i + 1,
              name: `Pilot ${i + 1}`,
              rank: FinestHour.getRandomGermanPilotQuality(i),
              aircraftType: allAircrafts.find((a) => a.type === sortie.bomberType).type,
            })),
        })]
        : []),
      ...new Array(escortSchwarms).fill(0)
        .map((x, i) => new Squadron({
          country: germany,
          name: `Schwarm ${i + 1 + (bomberCount > 0 ? 1 : 0)}`,
          color: colors[i + (bomberCount > 0 ? 1 : 0)],
          pilots: new Array(4).fill(0)
            .map((b, j) => ({
              id: j + 1,
              name: `Pilot ${j + 1}`,
              rank: FinestHour.getRandomGermanPilotQuality(j),
              aircraftType: allAircrafts.find((a) => a.type === sortie.escortType).type,
            })),
        })),
    ];

    sortie.germanSections = germanSections;

    // Where is it taking place?
    const roll = Dice.d6() + this.currentPhase;
    if (roll <= 5) {
      sortie.location = SortieLocations.overTheSea;
    } else {
      switch (this.currentPhase) {
        case 1:
          sortie.location = Math.random() <= 0.8
            ? SortieLocations.overEngland
            : SortieLocations.overFrance;
          break;
        case 2:
          sortie.location = Math.random() <= 0.6
            ? SortieLocations.overEngland
            : SortieLocations.overFrance;
          break;
        case 3:
          sortie.location = Math.random() <= 0.4
            ? SortieLocations.overEngland
            : SortieLocations.overFrance;
          break;
        case 4:
          sortie.location = Math.random() <= 0.2
            ? SortieLocations.overEngland
            : SortieLocations.overFrance;
          break;
        default:
          break;
      }
    }

    this.sorties = [new Sortie(sortie), ...this.sorties];
  }

  static getRandomGermanPilotQuality(i) {
    const rottenMan = ['VET', 'VET', 'REG', 'REG', 'REG', 'REG', 'Sprog', 'Sprog', 'Sprog', 'Sprog'];
    const rottenfuhrer = ['JA', 'JA', 'VET', 'VET', 'VET', 'VET', 'VET', 'VET', 'REG', 'REG'];
    const schwarmfuhrer = ['TA', 'JA', 'JA', 'VET', 'VET', 'VET', 'VET', 'VET', 'VET', 'VET'];
    // const stabfuhrer = ['TA', 'TA', 'JA', 'JA', 'JA', 'VET', 'VET', 'VET', 'VET', 'VET'];

    switch (i) {
      case 0:
        return schwarmfuhrer[Dice.d10() - 1];
      case 2:
        return rottenfuhrer[Dice.d10() - 1];
      default:
        return rottenMan[Dice.d10() - 1];
    }
  }

  rollForReinforcement() {
    const roll = Dice.d6(2);
    switch (roll) {
      case 1:
      case 2:
      case 3:
      case 4:
      case 5:
      case 6:
      case 7:
      case 8:
      case 9:
        this.lastSortie.reinforcementsStatus = 'No reinforcements arrive';
        this.lastSortie.reinforcementsArrived = false;
        break;
      case 10: {
        this.lastSortie.reinforcementsStatus = 'A blind arrives';
        this.lastSortie.reinforcementsArrived = true;
        this.generateReinforcement(1);
        break;
      }
      case 11: {
        const fighters = Dice.d6();
        this.lastSortie.reinforcementsStatus = `${fighters} Luftwaffe fighters arrive`;
        this.lastSortie.reinforcementsArrived = true;
        this.generateReinforcement(fighters);
        break;
      }
      case 12: {
        const fighters = Dice.d6(2);
        this.lastSortie.reinforcementsStatus = `${fighters} Luftwaffe fighters arrive`;
        this.lastSortie.reinforcementsArrived = true;
        this.generateReinforcement(fighters);
        break;
      }
      default:
        break;
    }
  }

  generateReinforcement(pilotCount) {
    const colors = ['Red', 'Blue', 'Green', 'Yellow', 'Black', 'Grey'];
    const germany = Countries.find((c) => c.name === 'Germany');

    const germanReinforcementSections = [
      ...new Array(1).fill(0)
        .map(() => new Squadron({
          country: germany,
          name: 'Schwarm 1',
          color: colors[1],
          pilots: new Array(pilotCount).fill(0)
            .map((b, j) => new Pilot({
              id: j + 1,
              name: `Pilot ${j + 1}`,
              rank: FinestHour.getRandomGermanPilotQuality(j),
              aircraft: allAircrafts
                .find((a) => a.type === this.lastSortie.reinforcementType),
            })),
        })),
    ];
    this.lastSortie.germanReinforcementSections = germanReinforcementSections;
    this.save();
  }

  generateReplacementPilot() {
    const pilotQuality = [
      ['VET', 'VET', 'REG', 'REG', 'REG', 'Sprog'],
      ['VET', 'REG', 'REG', 'REG', 'Sprog', 'Sprog'],
      ['VET', 'REG', 'REG', 'Sprog', 'Sprog', 'Sprog'],
      ['JA', 'VET', 'REG', 'Sprog', 'Sprog', 'Sprog'],
    ];
    const pilot = {
      id: uuidv4(),
      name: PilotNames[this.pilotNameIndex += 1],
      status: PilotStatus.active,
      rank: FinestHour.generateRank(),
      quality: pilotQuality[this.currentPhase - 1][Dice.d6() - 1],
    };
    this.pilots.push(pilot);
    this.squadron.numberOfAircrafts += 1;
    return pilot;
  }

  generateReplacementSquadronOfficer() {
    this.generateSquadronLeader();
  }

  getRandomActivePilot() {
    const pilot = this.activePilots[Math.floor(Math.random() * this.activePilots.length)];
    return pilot;
  }

  autoplay() {
    let rollModifier = 0;
    if (this.lastSortie.missionType.includes('is bounced')) {
      rollModifier = -2;
    }
    if (this.lastSortie.missionType.includes('unescorted bombers')) {
      rollModifier = 2;
    }

    [...this.nonEmptyActiveSlots].forEach((slot) => {
      const roll = Dice.d6(2) + rollModifier
        + (slot.pilot.quality === 'TA' ? 2 : 0)
        + (slot.pilot.quality === 'JA' ? 1 : 0)
        + (slot.pilot.quality === 'Sprog' ? -1 : 0);
      if (roll < 4) {
        slot.pilot.status = PilotStatus.killed; // eslint-disable-line
        this.lastSortie.destroyedAircrafts += 1;
      } else if (roll < 9) {
        // Nothing happens.
      } else if (roll < 11) {
        this.lastSortie.damagedAircrafts += 1;
      } else if (roll < 13) {
        slot.pilot.kills = (slot.pilot.kills || 0) + 1; // eslint-disable-line
        this.lastSortie.addLogEntry(`${slot.pilot.name} gets 1 kill. Total kills: ${slot.pilot.kills}`);
      } else {
        slot.pilot.kills = (slot.pilot.kills || 0) + 2; // eslint-disable-line
        this.lastSortie.addLogEntry(`${slot.pilot.name} gets 2 kills. Total kills: ${slot.pilot.kills}`);
      }
    });

    this.endSortie();
  }

  autoplayOneWeek() {
    const targetDate = moment(this.currentDate)
      .add(7, 'days');
    const inter = setInterval(() => {
      if (targetDate.isSameOrBefore(this.currentDate)) {
        clearInterval(inter);
      }
      this.autoplay();
    }, 100);
  }

  endSortie() {
    //
    // Apply destroyed / damaged quantities
    //
    for (let i = 0; i < this.lastSortie.destroyedAircrafts; i += 1) {
      if (this.activeAircrafts.length > 0) {
        this.activeAircrafts[0].isDestroyed = true;
      }
    }
    if (this.lastSortie.destroyedAircrafts > 0) {
      this.lastSortie.addLogEntry(`${this.lastSortie.destroyedAircrafts} destroyed aircraft${this.lastSortie.destroyedAircrafts > 1 ? 's' : ''}.`);
    }
    for (let i = 0; i < this.lastSortie.damagedAircrafts; i += 1) {
      if (this.activeAircrafts.length > 0) {
        this.activeAircrafts[0].isUnderRepair = true;
      }
    }
    if (this.lastSortie.damagedAircrafts > 0) {
      this.lastSortie.addLogEntry(`${this.lastSortie.damagedAircrafts} damaged aircraft${this.lastSortie.damagedAircrafts > 1 ? 's' : ''}.`);
    }

    //
    // Pilot progression
    //
    const pilotForWhichKillCountChanged = [];
    this.lastSortie.killsByPilot.forEach((x) => {
      const pilot = this.pilots.find((p) => p.id === x.id);
      const before = pilot.kills || 0;
      pilot.kills = before + x.count;
      pilotForWhichKillCountChanged.push(pilot);
    });
    this.lastSortie.aircraftsDestroyedByPilot.forEach((x) => {
      const pilot = this.pilots.find((p) => p.id === x.id);
      const before = pilot.kills || 0;
      pilot.kills = before + x.count * 0.5;
      pilotForWhichKillCountChanged.push(pilot);
    });
    this.lastSortie.aircraftsDamagedByPilot.forEach((x) => {
      const pilot = this.pilots.find((p) => p.id === x.id);
      const before = pilot.probable || 0;
      pilot.probable = before + x.count;
      pilotForWhichKillCountChanged.push(pilot);
    });
    pilotForWhichKillCountChanged.reduce((mem, p) => (mem.includes(p) ? mem : [...mem, p]), [])
      .forEach((p) => {
        this.lastSortie.addLogEntry(`${p.name}'s kill count is now ${p.kills || 0} confirmed, ${p.probable || 0} probable.`);
      });

    const sprogsBeingPromotedToRegular = this.pilots
      .filter((pilot) => pilot.quality === 'Sprog' && pilot.kills > 0);
    sprogsBeingPromotedToRegular.forEach((pilot) => {
      pilot.quality = 'REG'; // eslint-disable-line
      this.lastSortie.addLogEntry(`${pilot.name} get promoted to REG.`);
    });

    const regularsBeingPromotedToVeteran = this.pilots
      .filter((pilot) => pilot.quality === 'REG' && pilot.kills > 2);
    regularsBeingPromotedToVeteran.forEach((pilot) => {
      pilot.quality = 'VET'; // eslint-disable-line
      this.lastSortie.addLogEntry(`${pilot.name} get promoted to VET.`);
    });

    const veteransBeingPromotedToJuniorAce = this.pilots
      .filter((pilot) => pilot.quality === 'VET' && pilot.kills > 4);
    veteransBeingPromotedToJuniorAce.forEach((pilot) => {
      pilot.quality = 'JA'; // eslint-disable-line
      this.lastSortie.addLogEntry(`${pilot.name} get promoted to JA.`);
    });

    const juniorAcesBeingPromotedToTopAce = this.pilots
      .filter((pilot) => pilot.quality === 'JA' && pilot.kills > 9);
    juniorAcesBeingPromotedToTopAce.forEach((pilot) => {
      pilot.quality = 'TA'; // eslint-disable-line
      this.lastSortie.addLogEntry(`${pilot.name} get promoted to TA.`);
    });

    //
    // Is there another sortie that day?
    //
    const roll = Dice.d6()
      + (this.currentPhase === 2 ? 1 : 0)
      + (this.currentPhase === 3 ? 2 : 0)
      + (this.group.includes('11') ? 1 : 0)
      - this.sorties.filter((x) => moment(x.date)
        .isSame(this.currentDate)).length;
    if (roll > 4) {
      this.generateSortie();
      this.refillSquadron();
      this.save();
      return;
    }

    //
    // Daily events
    //
    let skipDays = 0;
    const diceResult = Dice.d6(2);
    switch (diceResult) {
      case 2: {
        this.lastSortie.otherDailyEvents = 'Squadron CO is posted to another squadron. Leaves today. Senior officer acts as CO until replacement arrives.';
        const co = this.activePilots.find((x) => x.rank === 'Flight Lieutenant');
        const coSlot = this.squadronSlots.find((x) => x.pilot === co);
        if (coSlot != null) {
          coSlot.pilot = null;
        }
        co.status = PilotStatus.transferred;
        const date = moment(this.currentDate)
          .add(Dice.d6(), 'days');
        this.dailyEvents.push(new DailyEvent({
          date,
          methodName: 'generateReplacementSquadronOfficer',
        }));
        this.lastSortie.otherDailyEventsResult = `${co.name}, ${co.rank}, ${co.quality} replaced on ${date.format('LL')}`;
        break;
      }
      case 3:
        this.lastSortie.otherDailyEvents = 'One aircraft suffers mechanical breakdown';
        this.dailyEventOneAircraftSuffersMechanicalDamage();
        break;
      case 4: {
        this.lastSortie.otherDailyEvents = 'Pilot rotated to another squadron. Leaves immediately.';
        const pilot = this.getRandomActivePilot();
        const slot = this.squadronSlots.find((x) => x.pilot === pilot);
        if (slot != null) {
          slot.pilot = null;
        }
        pilot.status = PilotStatus.transferred;
        this.lastSortie.otherDailyEventsResult = `${pilot.name}, ${pilot.rank}, ${pilot.quality}`;
        break;
      }
      case 5:
        switch (this.currentPhase) {
          case 1:
            this.dailyEventOnePilotOnLeave();
            break;
          case 2:
          case 3:
            this.noNotableDailyEvents();
            break;
          case 4:
            this.dailyEventOnePilotOnLeave();
            break;
          default:
            break;
        }

        break;
      case 6:
      case 7:
      case 8:
        this.noNotableDailyEvents();
        break;
      case 9:
        if (this.currentPhase === 2) {
          this.lastSortie.otherDailyEvents = 'Airfield is bombed. One aircraft is damaged.';
          this.dailyEventAirfieldBombed(1);
        } else {
          this.noNotableDailyEvents();
        }
        break;
      case 10:
        switch (this.currentPhase) {
          case 1:
          case 2:
          case 3:
            this.dailyEventNewPilotAssigned();
            break;
          case 4:
            this.dailyEventTwoNewPilotsAssigned();
            break;
          default:
            break;
        }
        break;
      case 11:
        switch (this.currentPhase) {
          case 1:
            this.dailyEventNewPilotAssigned();
            break;
          case 2:
            break;
          case 3:
            this.lastSortie.otherDailyEvents = 'Airfield is bombed. 2 planes is damaged.';
            this.dailyEventAirfieldBombed(2);
            break;
          case 4:
            this.lastSortie.otherDailyEvents = 'Airfield is bombed. D4 planes damaged.';
            this.dailyEventAirfieldBombed(Dice.d4());
            break;
          default:
            break;
        }
        break;
      case 12:
        switch (this.currentPhase) {
          case 1:
            this.dailyEventNewPilotAssigned();
            this.dailyEventOneAircraftSuffersMechanicalDamage();
            this.lastSortie.otherDailyEvents = 'New pilot assigned One aircraft suffers mechanical breakdown';
            break;
          case 2:
            this.dailyEventAirfieldBombed(Dice.d4());
            if (Dice.d6() === 6) {
              const pilot = this.getRandomActivePilot();
              pilot.status = PilotStatus.killed;
              this.lastSortie.otherDailyEventsResult += `<br>${pilot.name}, ${pilot.rank}, ${pilot.quality}`;
            }
            skipDays = 1;
            break;
          case 3:
          case 4:
            this.dailyEventMoveSquadron();
            skipDays = 2;
            break;
          default:
            break;
        }
        break;
      default:
        break;
    }
    this.nextDay(skipDays);
  }

  dailyEventMoveSquadron() {
    this.lastSortie.otherDailyEvents = 'Squadron is moved to another airfield. Spend next two days moving.';
    const roll = Dice.d6(2) + 1;
    if (roll > 9) {
      this.lastSortie.otherDailyEventsResult = `${this.group}, ${this.homeAirfield} => `;
      const group = Groups[roll - 10];
      this.groupID = group.id;
      this.group = group.name;
      this.generateHomeAirfield();
      this.lastSortie.otherDailyEventsResult += `${this.group}, ${this.homeAirfield}`;
    }
  }

  dailyEventAirfieldBombed(numberOfPlanes = 1) {
    // One aircraft damaged.
    let damaged = 0;
    let destroyed = 0;
    for (let i = 0; i < numberOfPlanes; i += 1) {
      if (this.activeAircrafts.length > 0) {
        const aircraft = this.activeAircrafts[0];
        if (Dice.d6() === 6) {
          aircraft.isDestroyed = true;
          destroyed += 1;
        } else {
          aircraft.isUnderRepair = true;
          damaged += 1;
        }
      }
    }
    this.lastSortie.otherDailyEventsResult = `${damaged} damaged, ${destroyed} destroyed`;
  }

  dailyEventOneAircraftSuffersMechanicalDamage() {
    const aircraft = this.aircrafts.find((x) => !x.isUnderRepair);
    if (aircraft != null) {
      aircraft.isUnderRepair = true;
    }
    return aircraft;
  }

  dailyEventOnePilotOnLeave() {
    this.lastSortie.otherDailyEvents = 'One pilot selected at random is granted leave for d6 days. Leaves immediately';
    const pilot = this.activePilots[Math.floor(Math.random() * this.activePilots.length)];
    const slot = this.squadronSlots.find((x) => x.pilot === pilot);
    if (slot != null) {
      slot.pilot = null;
    }
    pilot.status = PilotStatus.onLeave;
    pilot.backOn = moment(this.currentDate)
      .add(Dice.d6(), 'days');
    this.lastSortie.otherDailyEventsResult = `${pilot.name}, ${pilot.rank}, ${pilot.quality} back on ${pilot.backOn.format('LL')}`;
  }

  dailyEventNewPilotAssigned() {
    this.lastSortie.otherDailyEvents = 'New pilot assigned';
    const pilot = this.generateReplacementPilot();
    this.lastSortie.otherDailyEventsResult = `${pilot.name}, ${pilot.rank}, ${pilot.quality}`;
  }

  dailyEventTwoNewPilotsAssigned() {
    this.lastSortie.otherDailyEvents = 'Two new pilots assigned';
    let pilot = this.generateReplacementPilot();
    this.lastSortie.otherDailyEventsResult = `${pilot.name}, ${pilot.rank}, ${pilot.quality}`;
    pilot = this.generateReplacementPilot();
    this.lastSortie.otherDailyEventsResult += `<br>${pilot.name}, ${pilot.rank}, ${pilot.quality}`;
  }

  noNotableDailyEvents() {
    this.lastSortie.otherDailyEvents = 'No notable events';
  }

  nextDay(skipDays = 0) {
    for (let i = 0; i < 1 + skipDays; i += 1) {
      this.currentDate = moment(this.currentDate)
        .add(1, 'day');
      this.runDailyEvents();
    }
    // Pilots back from leave
    [...this.pilots.filter((x) => x.status === PilotStatus.onLeave)].forEach((x) => {
      if (moment(x.backOn)
        .isSameOrBefore(this.currentDate)) {
        x.status = PilotStatus.active; // eslint-disable-line
        this.lastSortie.addLogEntry(`${x.name} is back from leave.`);
      }
    });
    // Pilots back from being wounded
    [...this.pilots.filter((x) => x.status === PilotStatus.injured)].forEach((x) => {
      if (moment(x.backOn)
        .isSameOrBefore(this.currentDate) && Dice.d6() > 2) {
        x.status = PilotStatus.active; // eslint-disable-line
        this.lastSortie.addLogEntry(`${x.name} is back from begin wounded.`);
      }
    });
    // Pilots who bailed out
    [...this.pilots.filter((x) => x.status === PilotStatus.bailedOut)].forEach((x) => {
      if (moment(x.backOn)
        .isSameOrBefore(this.currentDate)) {
        x.status = PilotStatus.active; // eslint-disable-line
        this.lastSortie.addLogEntry(`${x.name} is back from bail out.`);
      }
    });
    this.repairAircrafts();
    this.generateSortie();
    this.refillSquadron();
    this.save();
  }

  repairAircrafts() {
    const underRepair = [...this.aircraftsUnderRepair];
    if (underRepair.length === 0) {
      return;
    }
    const roll = Dice.d6() - underRepair.length;
    let repairUpToXAircrafts = 1;
    if (roll === 0) {
      repairUpToXAircrafts = 3;
    } else if (roll === 1) {
      repairUpToXAircrafts = 2;
    }
    const aircraftsGettingRepaired = Math.min(repairUpToXAircrafts, underRepair.length);
    for (let i = 0; i < aircraftsGettingRepaired; i += 1) {
      underRepair[i].isUnderRepair = false;
    }
    this.lastSortie.addLogEntry(`${aircraftsGettingRepaired} aircraft${aircraftsGettingRepaired > 1
      ? 's' : ''} get${aircraftsGettingRepaired === 1
      ? 's' : ''} repaired.`);
  }

  refillSquadron() {
    const slotsToClear = this.nonEmptySlots.filter((x) => x.pilot.status !== PilotStatus.active);
    slotsToClear.forEach((x) => {
      x.pilot = null; // eslint-disable-line
    });

    const squadronLeaderSlot = this.squadronSlots.find((x) => x.type === 'Squadron Leader');
    const flightLeaderSlot = this.squadronSlots.find((x) => x.type === 'Flight Leader');
    const sortByQuality = (a, b) => {
      if (PilotQuality.indexOf(a.quality) < PilotQuality.indexOf(b.quality)) {
        return -1;
      }
      if (PilotQuality.indexOf(a.quality) > PilotQuality.indexOf(b.quality)) {
        return 1;
      }
      return 0;
    };
    const getSectionLeaders = () => this.nonEmptySlots
      .filter((x) => x.type == null && x.position === 1)
      .sort(sortByQuality);

    if (squadronLeaderSlot.pilot == null) {
      const roll = Dice.d6();
      if (roll < 5) {
        squadronLeaderSlot.pilot = this.generateSquadronLeader();
        this.squadron.numberOfAircrafts += 1;
        this.lastSortie.addLogEntry(`${squadronLeaderSlot.pilot.name} is recruited as new squadron leader.`);
      } else if (flightLeaderSlot.pilot != null) {
        squadronLeaderSlot.pilot = flightLeaderSlot.pilot;
        flightLeaderSlot.pilot = null;
      } else {
        const sectionLeaders = getSectionLeaders();
        if (sectionLeaders.length > 0) {
          squadronLeaderSlot.pilot = sectionLeaders[0].pilot;
          sectionLeaders[0].pilot = null;
        }
      }
    }
    if (flightLeaderSlot.pilot == null) {
      const roll = Dice.d6();
      if (roll < 5) {
        flightLeaderSlot.pilot = this.generateFlightLeader();
        this.squadron.numberOfAircrafts += 1;
        this.lastSortie.addLogEntry(`${flightLeaderSlot.pilot.name} is recruited as new flight leader.`);
      } else {
        const sectionLeaders = getSectionLeaders();
        if (sectionLeaders.length > 0) {
          squadronLeaderSlot.pilot = sectionLeaders[0].pilot;
          sectionLeaders[0].pilot = null;
        }
      }
    }

    //
    // Assign pilots to empty slots.
    //
    let emptyActiveSlots = [...this.emptyActiveSlots];
    emptyActiveSlots.forEach((s) => {
      if (s.type == null) {
        const slot = this.nonEmptyInactiveSlots.filter((x) => x.type == null)[
          Math.floor(Math.random() * this.nonEmptyInactiveSlots
            .filter((x) => x.type == null).length)];
        if (slot != null) {
          s.pilot = slot.pilot; // eslint-disable-line
          slot.pilot = null;
        }
      }
    });

    //
    // Move pilots to empty slots (even leaders)
    //
    emptyActiveSlots = [...this.emptyActiveSlots];
    if (emptyActiveSlots.length > 0 && this.nonEmptyInactiveSlots.length > 0) {
      const nonEmptyInactiveSlots = [...this.nonEmptyInactiveSlots];
      nonEmptyInactiveSlots.sort((s) => PilotQuality.indexOf(s.pilot.quality)
        + PilotRank.indexOf(s.pilot.rank));

      emptyActiveSlots.forEach((s, i) => {
        if (nonEmptyInactiveSlots.length > i) {
          s.pilot = nonEmptyInactiveSlots[i].pilot; // eslint-disable-line
          nonEmptyInactiveSlots[i].pilot = null;
        }
      });

      //
      // Reorder pilots.
      //
      const sortedByRank = [...this.nonEmptyActiveSlots]
        .sort((s) => PilotQuality.indexOf(s.pilot.quality)
          + PilotRank.indexOf(s.pilot.rank));
      [...this.nonEmptyActiveSlots].sort((s) => s.position)
        .forEach((s, i) => {
          s.pilot = sortedByRank[i].pilot; // eslint-disable-line
        });
    }

    const emptySlots = [...this.emptySlots];
    emptySlots.forEach((s) => {
      if (this.unassignedPilots.length > 0) {
        [s.pilot] = this.unassignedPilots; // eslint-disable-line
      }
    });
  }

  runDailyEvents() {
    const events = this.dailyEvents.filter((x) => x.date.isSame(this.currentDate));
    events.forEach((x) => {
      this[x.methodName]();
    });
  }
}
